import React, { useEffect, useMemo, useState } from 'react';

const ALL_PARENT_GROUPS = 'All Parent Groups';

const COLUMNS = [
  { key: 'parentGroup', label: 'Parent Group' },
  { key: 'subGroupDescription', label: 'Sub-Group Description' },
  { key: 'ticketCount', label: 'Ticket Count' },
  { key: 'totalEffortHours', label: 'Total Effort (Hours)' },
  { key: 'rootCause', label: 'Detailed Root Cause' },
  { key: 'permanentSolution', label: 'Permanent Solution / Preventive Action' },
];

function getSortValue(row, key) {
  if (key === 'ticketCount' || key === 'totalEffortHours') return Number(row[key]) || 0;
  const value = row[key];
  if (value == null || value === '') return '';
  return String(value).toLowerCase();
}

function compareSortValues(aVal, bVal, direction) {
  const dir = direction === 'asc' ? 1 : -1;
  const aEmpty = aVal === '';
  const bEmpty = bVal === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (typeof aVal === 'number' && typeof bVal === 'number') {
    return (aVal - bVal) * dir;
  }
  return String(aVal).localeCompare(String(bVal), undefined, { numeric: true }) * dir;
}

function SortIndicator({ active, direction }) {
  if (!active) {
    return (
      <span className="nlp-sort-indicator nlp-sort-indicator-idle" aria-hidden="true">
        ↕
      </span>
    );
  }
  return (
    <span className="nlp-sort-indicator" aria-hidden="true">
      {direction === 'asc' ? '↑' : '↓'}
    </span>
  );
}

function Section2HierarchicalAnalysis({ rows, filtersApplied, groupingDone, isRunning, onRun }) {
  const [sortConfig, setSortConfig] = useState({ key: 'parentGroup', direction: 'asc' });
  const [parentGroupFilter, setParentGroupFilter] = useState(ALL_PARENT_GROUPS);

  const parentGroupOptions = useMemo(() => {
    const unique = Array.from(
      new Set((rows || []).map((row) => row.parentGroup).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return [ALL_PARENT_GROUPS, ...unique];
  }, [rows]);

  useEffect(() => {
    setParentGroupFilter(ALL_PARENT_GROUPS);
    setSortConfig({ key: 'parentGroup', direction: 'asc' });
  }, [rows]);

  const displayedRows = useMemo(() => {
    const filtered =
      parentGroupFilter === ALL_PARENT_GROUPS
        ? rows
        : rows.filter((row) => row.parentGroup === parentGroupFilter);

    const { key, direction } = sortConfig;
    return [...filtered].sort((a, b) =>
      compareSortValues(getSortValue(a, key), getSortValue(b, key), direction)
    );
  }, [rows, parentGroupFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  return (
    <>
      <div className="nlp-grouping-run-wrap">
        <button
          type="button"
          className="nlp-grouping-run-btn"
          onClick={onRun}
          disabled={isRunning || !filtersApplied}
        >
          {isRunning ? 'Running hierarchical grouping…' : 'Run Hierarchical Grouping & Sub-RCA'}
        </button>
      </div>

      {groupingDone && (
        <section className="nlp-grouping-card">
          <h2 className="nlp-section-title">2. Hierarchical Sub-Group Analysis &amp; Permanent Solutions</h2>

          {rows.length === 0 ? (
            <p className="nlp-grouping-empty">No hierarchical groups returned for the selected filters.</p>
          ) : (
            <>
              <div className="nlp-ticket-toolbar">
                <label className="nlp-ticket-filter">
                  <span className="nlp-filter-label">Filter by Parent Group</span>
                  <select
                    value={parentGroupFilter}
                    onChange={(e) => setParentGroupFilter(e.target.value)}
                  >
                    {parentGroupOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
                <span className="nlp-ticket-count">
                  Showing {displayedRows.length} of {rows.length} groups
                </span>
              </div>

              <div className="nlp-table-wrap nlp-table-wrap-wide">
                <table className="nlp-data-table">
                  <thead>
                    <tr>
                      {COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          className="nlp-sortable-th"
                          onClick={() => handleSort(col.key)}
                          aria-sort={
                            sortConfig.key === col.key
                              ? sortConfig.direction === 'asc'
                                ? 'ascending'
                                : 'descending'
                              : 'none'
                          }
                        >
                          <span className="nlp-th-label">
                            {col.label}
                            <SortIndicator
                              active={sortConfig.key === col.key}
                              direction={sortConfig.direction}
                            />
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayedRows.length === 0 ? (
                      <tr>
                        <td colSpan={COLUMNS.length} className="nlp-grouping-empty-cell">
                          No groups match the selected parent group filter.
                        </td>
                      </tr>
                    ) : (
                      displayedRows.map((row) => (
                        <tr key={row.id}>
                          <td>{row.parentGroup}</td>
                          <td>{row.subGroupDescription}</td>
                          <td className="nlp-td-num">{row.ticketCount}</td>
                          <td className="nlp-td-num">{(row.totalEffortHours || 0).toLocaleString()}</td>
                          <td>{row.rootCause}</td>
                          <td>{row.permanentSolution}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}
    </>
  );
}

export default Section2HierarchicalAnalysis;
